import { useState, type ChangeEvent, type FormEvent, type JSX } from 'react';
import { ContactSchema } from '../lib/schemas/forms';
import type { ContactForm } from '../types/forms';

const ADDRESS = 'Kalakunj, Plot 190, Prashasan Nagar, Jubilee Hills, Hyderabad';
const MAP_EMBED_SRC = `https://www.google.com/maps?q=${encodeURIComponent(ADDRESS)}&output=embed`;

type FieldErrors = Partial<Record<keyof ContactForm, string[]>>;

const initialForm: ContactForm = { name: '', email: '', phone: '', message: '' };

export default function ContactMe(): JSX.Element {
  const [form, setForm] = useState<ContactForm>(initialForm);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  function handleChange(
    field: keyof ContactForm,
  ): (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void {
    return (event): void => {
      setForm((prev) => ({ ...prev, [field]: event.target.value }));
    };
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();

    const result = ContactSchema.safeParse(form);
    if (!result.success) {
      setErrors(result.error.flatten().fieldErrors);
      setStatus('error');
      return;
    }

    setErrors({});
    // TODO: no email delivery service is wired up yet (EmailJS vs. a small API
    // endpoint is still an open decision — see rewrite.md Phase 4).
    setStatus('success');
    setForm(initialForm);
  }

  return (
    <main className="mx-auto max-w-5xl px-4 py-12">
      <h1 className="text-2xl font-bold text-neutral-900">Contact Me</h1>

      <div className="mt-6 grid grid-cols-1 gap-8 md:grid-cols-2">
        <div>
          <h2 className="font-semibold text-neutral-900">Location</h2>
          <iframe
            title="Shaping Colours location"
            src={MAP_EMBED_SRC}
            className="mt-2 h-72 w-full rounded-md border border-neutral-200"
            loading="lazy"
          />

          <h2 className="mt-6 font-semibold text-neutral-900">Contact Information</h2>
          <p className="mt-2 text-neutral-700">{ADDRESS}</p>
          <p className="mt-2 text-neutral-700">Phone: +91-8790714106</p>
          <p className="text-neutral-700">Email: enquiry@shapingcolours.com</p>
        </div>

        <div>
          <h2 className="font-semibold text-neutral-900">Enquiry</h2>

          {status === 'success' ? (
            <p className="mt-4 rounded-md bg-green-50 p-4 text-green-800">
              Thanks for reaching out — we&apos;ll get back to you soon.
            </p>
          ) : (
            <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-1 block text-sm text-neutral-700">
                    Name
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    value={form.name}
                    onChange={handleChange('name')}
                    className="w-full border-b border-neutral-300 py-1 text-sm focus:outline-none focus:border-blue-600"
                  />
                  {errors.name?.[0] !== undefined ? (
                    <p className="mt-1 text-xs text-red-600">{errors.name[0]}</p>
                  ) : null}
                </div>

                <div>
                  <label htmlFor="contact-email" className="mb-1 block text-sm text-neutral-700">
                    Email
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    value={form.email}
                    onChange={handleChange('email')}
                    className="w-full border-b border-neutral-300 py-1 text-sm focus:outline-none focus:border-blue-600"
                  />
                  {errors.email?.[0] !== undefined ? (
                    <p className="mt-1 text-xs text-red-600">{errors.email[0]}</p>
                  ) : null}
                </div>

                <div>
                  <label htmlFor="contact-phone" className="mb-1 block text-sm text-neutral-700">
                    Phone
                  </label>
                  <input
                    id="contact-phone"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange('phone')}
                    className="w-full border-b border-neutral-300 py-1 text-sm focus:outline-none focus:border-blue-600"
                  />
                  {errors.phone?.[0] !== undefined ? (
                    <p className="mt-1 text-xs text-red-600">{errors.phone[0]}</p>
                  ) : null}
                </div>

                <div>
                  <label htmlFor="contact-message" className="mb-1 block text-sm text-neutral-700">
                    Message
                  </label>
                  <input
                    id="contact-message"
                    type="text"
                    value={form.message}
                    onChange={handleChange('message')}
                    className="w-full border-b border-neutral-300 py-1 text-sm focus:outline-none focus:border-blue-600"
                  />
                  {errors.message?.[0] !== undefined ? (
                    <p className="mt-1 text-xs text-red-600">{errors.message[0]}</p>
                  ) : null}
                </div>
              </div>

              <button
                type="submit"
                className="mt-4 rounded bg-blue-600 px-5 py-2.5 text-white hover:bg-blue-700"
              >
                Submit
              </button>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}
